import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {MapPin, CheckCircle, XCircle} from 'lucide-react'

/**
 * ezShip 電子地圖回傳頁面
 * 接收門市選擇結果，存儲到 localStorage 並通知父窗口，然後自動關閉
 */
const EzShipCallbackPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [storeInfo, setStoreInfo] = useState<any>(null)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    console.log('🚀 EzShipCallbackPage 已載入')
    console.log('📍 當前 URL:', window.location.href)
    console.log('🔗 完整路徑:', location.pathname + location.search)
    
    try {
      // 從 URL 參數中獲取 ezShip 回傳的門市資料
      const params = new URLSearchParams(location.search)
      
      // ezShip 回傳的主要參數：
      // stCate: 超商代碼 (TLF=萊爾富, FAMI=全家, UNIMART=7-11, HILIFE=OK)
      // stCode: 門市代碼
      // stName: 門市名稱
      // stAddr: 門市地址
      // stTel: 門市電話
      
      const stCate = params.get('stCate')
      const stCode = params.get('stCode')
      const stName = params.get('stName')
      const stAddr = params.get('stAddr')
      const stTel = params.get('stTel')
      const error = params.get('error')

      console.log('📦 ezShip 回傳參數:', {
        stCate,
        stCode,
        stName,
        stAddr,
        stTel,
        error,
        hasOpener: !!window.opener,
        openerClosed: window.opener?.closed
      })

      // 檢查是否有錯誤參數
      if (error) {
        console.error('❌ 收到錯誤參數:', error)
        setStatus('error')
        if (error === 'missing_data') {
          setErrorMessage('門市資料不完整，請重新選擇')
        } else {
          setErrorMessage('處理門市資料時發生錯誤')
        }
        
        // 3 秒後自動關閉或返回
        setTimeout(() => {
          if (window.opener && !window.opener.closed) {
            window.close()
          } else {
            navigate('/zh/checkout')
          }
        }, 3000)
        return
      }

      // 檢查必要參數
      if (!stCate || !stCode || !stName || !stAddr) {
        console.error('❌ 缺少必要參數')
        setStatus('error')
        setErrorMessage('門市資料不完整，請重新選擇')
        
        // 3 秒後自動關閉或返回
        setTimeout(() => {
          if (window.opener && !window.opener.closed) {
            window.close()
          } else {
            navigate('/zh/checkout')
          }
        }, 3000)
        return
      }

      // 組合門市資訊
      const selectedStore = {
        stCate,
        stCode,
        stName,
        stAddr,
        stTel: stTel || ''
      }

      console.log('✅ 門市資料完整:', selectedStore)
      setStoreInfo(selectedStore)

      // 方案 A：存儲到 localStorage（供 CheckoutPage 讀取）
      try {
        localStorage.setItem('ezship_selected_store', JSON.stringify(selectedStore))
        console.log('💾 已存儲到 localStorage')
        
        // 驗證存儲
        const stored = localStorage.getItem('ezship_selected_store')
        console.log('✓ 驗證存儲成功:', stored ? '有資料' : '無資料')
      } catch (storageError) {
        console.error('❌ localStorage 存儲失敗:', storageError)
      }

      // 通知父窗口（雙重保險）
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(
            { 
              type: 'ezship_store_selected', 
              data: selectedStore 
            },
            window.location.origin
          )
          console.log('📤 已通過 postMessage 通知父窗口')
        } catch (error) {
          console.warn('⚠️ postMessage 失敗，但 localStorage 已保存:', error)
        }
      } else {
        console.warn('⚠️ 無父窗口或父窗口已關閉，僅使用 localStorage')
      }

      setStatus('success')

      // 2 秒後自動關閉視窗
      setTimeout(() => {
        console.log('🔄 準備關閉視窗')
        
        if (window.opener && !window.opener.closed) {
          window.close()
        } else {
          // 如果沒有父窗口，重定向回結帳頁
          console.log('🔄 無父窗口，重定向回結帳頁')
          navigate('/zh/checkout')
        }
        
        // 如果無法關閉（某些瀏覽器限制），提示用戶
        setTimeout(() => {
          if (!window.closed && window.opener) {
            setErrorMessage('請手動關閉此窗口')
          }
        }, 100)
      }, 2000)

    } catch (error) {
      console.error('❌ 處理門市資料時發生錯誤:', error)
      setStatus('error')
      setErrorMessage('處理門市資料時發生錯誤')
      
      // 3 秒後自動關閉或返回
      setTimeout(() => {
        if (window.opener && !window.opener.closed) {
          window.close()
        } else {
          navigate('/zh/checkout')
        }
      }, 3000)
    }
  }, [location, navigate])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-8 text-center">
        {/* 處理中 */}
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-xl font-light text-gray-800 dark:text-gray-200 mb-2">
              處理中...
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              正在接收門市資料
            </p>
          </>
        )}

        {/* 成功 */}
        {status === 'success' && storeInfo && (
          <>
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-light text-gray-800 dark:text-gray-200 mb-2">
              門市選擇成功！
            </h2>
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 p-4 mb-4 text-left">
              <div className="flex items-start space-x-3">
                <MapPin size={20} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                    {storeInfo.stName}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {storeInfo.stAddr}
                  </p>
                  {storeInfo.stTel && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      電話: {storeInfo.stTel}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              視窗將自動關閉...
            </p>
          </>
        )}

        {/* 錯誤 */}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-light text-gray-800 dark:text-gray-200 mb-2">
              發生錯誤
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {errorMessage || '無法處理門市資料'}
            </p>
            <button
              onClick={() => {
                if (window.opener) {
                  window.close()
                } else {
                  navigate('/zh/checkout')
                }
              }}
              className="px-6 py-2 bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-800 hover:bg-gray-700 dark:hover:bg-gray-300 transition-colors duration-200"
            >
              關閉視窗
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default EzShipCallbackPage
